<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import QaDashboardApi from 'dashboard/api/qaDashboard';
import '../styles/qaShell.css';

// Stable compile marker for release verification.
const QA_CALIBRATION_V1 = 'QA_CALIBRATION_V1';

const { t } = useI18n();
const store = useStore();

const isLoading = ref(false);
const errorText = ref('');
const report = ref(null);
const compileMarker = computed(() => QA_CALIBRATION_V1);

const toLocalInput = date => {
  const d = new Date(date);
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const filters = reactive({
  window_start: toLocalInput(Date.now() - 30 * 24 * 60 * 60 * 1000),
  window_end: toLocalInput(Date.now()),
});

const agents = computed(() => store.getters['agents/getAgents'] || []);
const teams = computed(() => store.getters['teams/getTeams'] || []);

const agentName = id => {
  const agent = agents.value.find(a => Number(a.id) === Number(id));
  if (!agent) return `#${id}`;
  return agent.name || agent.available_name || agent.email || `#${id}`;
};

const teamName = id => {
  const team = teams.value.find(tm => Number(tm.id) === Number(id));
  return team?.name || `#${id}`;
};

const humanize = key =>
  String(key)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, ch => ch.toUpperCase());

const pct = value => {
  if (value === null || value === undefined || value === '') return '—';
  return `${(Number(value) * 100).toFixed(1)}%`;
};

const ciLabel = stats => {
  if (stats.ci95_low === null || stats.ci95_low === undefined) return '';
  return `${pct(stats.ci95_low)} – ${pct(stats.ci95_high)}`;
};

const overall = computed(() => report.value?.overall || null);
const minSample = computed(() => report.value?.min_sample ?? 20);
const scope = computed(() => report.value?.scope || {});
const isScoped = computed(() => scope.value.scoped === true);
const isEmptyScope = computed(() => scope.value.empty_scope === true);
const hasData = computed(() => (overall.value?.sample_size || 0) > 0);

// Build the four grouped sections. Each row carries a resolved label plus the
// raw stats hash. Rows are sorted by sample size so the busiest cells surface.
const buildRows = (group, labelFn) => {
  if (!group) return [];
  return Object.entries(group)
    .map(([key, stats]) => ({ id: key, label: labelFn(key), ...stats }))
    .sort((a, b) => (b.sample_size || 0) - (a.sample_size || 0));
};

const sections = computed(() => {
  if (!report.value) return [];
  return [
    {
      key: 'detector',
      title: t('QA_DASHBOARD.CALIBRATION.SECTION_DETECTOR'),
      rows: buildRows(report.value.by_detector, humanize),
    },
    {
      key: 'category',
      title: t('QA_DASHBOARD.CALIBRATION.SECTION_CATEGORY'),
      rows: buildRows(report.value.by_category, humanize),
    },
    {
      key: 'agent',
      title: t('QA_DASHBOARD.CALIBRATION.SECTION_AGENT'),
      rows: buildRows(report.value.by_agent, agentName),
    },
    {
      key: 'team',
      title: t('QA_DASHBOARD.CALIBRATION.SECTION_TEAM'),
      rows: buildRows(report.value.by_team, teamName),
    },
  ];
});

const load = async () => {
  // Guard invalid/empty datetime-local before new Date(...).toISOString() throws a
  // RangeError that would surface as a misleading "server error".
  const startDate = new Date(filters.window_start);
  const endDate = new Date(filters.window_end);
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
    errorText.value = t('QA_DASHBOARD.CALIBRATION.INVALID_RANGE');
    return;
  }
  isLoading.value = true;
  errorText.value = '';
  try {
    const { data } = await QaDashboardApi.getCalibration({
      window_start: startDate.toISOString(),
      window_end: endDate.toISOString(),
    });
    report.value = data;
  } catch (error) {
    errorText.value = t('QA_DASHBOARD.CALIBRATION.ERROR_LOAD');
    useAlert(errorText.value);
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => {
  store.dispatch('agents/get');
  store.dispatch('teams/get');
  load();
});
</script>

<template>
  <div class="qa-page" :data-qa-compile-marker="compileMarker">
    <div class="qa-shell">
      <div class="qa-header">
        <div class="qa-header-left">
          <h1 class="qa-title">{{ t('QA_DASHBOARD.CALIBRATION.TITLE') }}</h1>
          <p class="qa-desc">{{ t('QA_DASHBOARD.CALIBRATION.DESCRIPTION') }}</p>
        </div>
        <div class="qa-header-actions">
          <button
            type="button"
            class="qa-btn"
            :disabled="isLoading"
            @click="load"
          >
            {{ t('QA_DASHBOARD.CALIBRATION.REFRESH') }}
          </button>
        </div>
      </div>

      <div class="qa-pill qa-pill--muted">
        {{ t('QA_DASHBOARD.CALIBRATION.OBSERVE_NOTE') }}
      </div>

      <div v-if="isScoped" class="qa-pill qa-pill--warn">
        {{ t('QA_DASHBOARD.CALIBRATION.SCOPED_CAVEAT') }}
      </div>

      <div class="qa-toolbar">
        <div class="qa-toolbar-left">
          <label class="qa-field">
            <span class="qa-label">{{
              t('QA_DASHBOARD.CALIBRATION.WINDOW_START')
            }}</span>
            <input
              v-model="filters.window_start"
              type="datetime-local"
              class="qa-input"
            />
          </label>
          <label class="qa-field">
            <span class="qa-label">{{
              t('QA_DASHBOARD.CALIBRATION.WINDOW_END')
            }}</span>
            <input
              v-model="filters.window_end"
              type="datetime-local"
              class="qa-input"
            />
          </label>
          <button
            type="button"
            class="qa-btn qa-btn--primary"
            :disabled="isLoading"
            @click="load"
          >
            {{ t('QA_DASHBOARD.CALIBRATION.APPLY') }}
          </button>
        </div>
      </div>

      <p v-if="errorText" class="qa-error">{{ errorText }}</p>

      <!-- Distinct empty states: no visibility scope vs no reviewed incidents -->
      <div v-if="report && isEmptyScope" class="qa-card">
        <div class="qa-card-body">
          <h3 class="qa-card-title">
            {{ t('QA_DASHBOARD.CALIBRATION.EMPTY_SCOPE_TITLE') }}
          </h3>
          <p class="qa-desc">
            {{ t('QA_DASHBOARD.CALIBRATION.EMPTY_SCOPE_BODY') }}
          </p>
        </div>
      </div>
      <div v-else-if="report && !hasData" class="qa-card">
        <div class="qa-card-body">
          <h3 class="qa-card-title">
            {{ t('QA_DASHBOARD.CALIBRATION.EMPTY_TITLE') }}
          </h3>
          <p class="qa-desc">{{ t('QA_DASHBOARD.CALIBRATION.EMPTY_BODY') }}</p>
        </div>
      </div>

      <template v-if="report && hasData">
        <!-- Overall confirmation KPIs -->
        <div class="qa-kpis">
          <div class="qa-kpi qa-kpi--tone-accent">
            <span class="qa-kpi-label">{{
              t('QA_DASHBOARD.CALIBRATION.KPI_RATE')
            }}</span>
            <span class="qa-kpi-value">{{ pct(overall.rate) }}</span>
            <span v-if="ciLabel(overall)" class="qa-kpi-suffix">
              {{ t('QA_DASHBOARD.CALIBRATION.CI_LABEL') }}
              {{ ciLabel(overall) }}
            </span>
          </div>
          <div class="qa-kpi">
            <span class="qa-kpi-label">{{
              t('QA_DASHBOARD.CALIBRATION.KPI_DECISIONS')
            }}</span>
            <span class="qa-kpi-value">
              {{ overall.denominator }}
              <span
                class="qa-badge"
                :class="
                  overall.sufficient_sample
                    ? 'qa-badge--good'
                    : 'qa-badge--warn'
                "
                >{{
                  overall.sufficient_sample
                    ? t('QA_DASHBOARD.CALIBRATION.BADGE_SUFFICIENT')
                    : t('QA_DASHBOARD.CALIBRATION.BADGE_INSUFFICIENT')
                }}</span
              >
            </span>
            <span class="qa-kpi-suffix">
              {{
                t('QA_DASHBOARD.CALIBRATION.MIN_SAMPLE_NOTE', { n: minSample })
              }}
              ·
              {{
                t('QA_DASHBOARD.CALIBRATION.TOTAL_REVIEWED_NOTE', {
                  n: overall.sample_size,
                })
              }}
            </span>
          </div>
          <div class="qa-kpi qa-kpi--tone-good">
            <span class="qa-kpi-label">{{
              t('QA_DASHBOARD.CALIBRATION.KPI_CONFIRMED')
            }}</span>
            <span class="qa-kpi-value">{{ overall.confirmed }}</span>
          </div>
          <div class="qa-kpi qa-kpi--tone-warn">
            <span class="qa-kpi-label">{{
              t('QA_DASHBOARD.CALIBRATION.KPI_OVERTURNED')
            }}</span>
            <span class="qa-kpi-value">{{ overall.overturned }}</span>
          </div>
          <div class="qa-kpi qa-kpi--tone-neutral">
            <span class="qa-kpi-label">{{
              t('QA_DASHBOARD.CALIBRATION.KPI_EXCUSED')
            }}</span>
            <span class="qa-kpi-value">{{ overall.excused }}</span>
          </div>
        </div>

        <p class="qa-hint">
          {{ t('QA_DASHBOARD.CALIBRATION.DECISIONS_HINT') }}
        </p>
        <p class="qa-hint">{{ t('QA_DASHBOARD.CALIBRATION.KAPPA_NOTE') }}</p>
        <p class="qa-hint">{{ t('QA_DASHBOARD.CALIBRATION.GROUP_NOTE') }}</p>

        <!-- Grouped breakdowns -->
        <div v-for="section in sections" :key="section.key" class="qa-card">
          <div class="qa-card-header">
            <h3 class="qa-card-title">{{ section.title }}</h3>
          </div>
          <div class="qa-card-body qa-card-body--flush">
            <div class="qa-table-wrap">
              <table class="qa-table">
                <thead>
                  <tr>
                    <th class="qa-th">
                      {{ t('QA_DASHBOARD.CALIBRATION.TH_KEY') }}
                    </th>
                    <th class="qa-th qa-th--center">
                      {{ t('QA_DASHBOARD.CALIBRATION.TH_SAMPLE') }}
                    </th>
                    <th class="qa-th qa-th--center">
                      {{ t('QA_DASHBOARD.CALIBRATION.TH_CONFIRMED') }}
                    </th>
                    <th class="qa-th qa-th--center">
                      {{ t('QA_DASHBOARD.CALIBRATION.TH_OVERTURNED') }}
                    </th>
                    <th class="qa-th qa-th--center">
                      {{ t('QA_DASHBOARD.CALIBRATION.TH_EXCUSED') }}
                    </th>
                    <th class="qa-th qa-th--center">
                      {{ t('QA_DASHBOARD.CALIBRATION.TH_RATE') }}
                    </th>
                    <th class="qa-th qa-th--center">
                      {{ t('QA_DASHBOARD.CALIBRATION.TH_STATUS') }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="!section.rows.length">
                    <td class="qa-td qa-td--empty" colspan="7">
                      {{ t('QA_DASHBOARD.CALIBRATION.GROUP_EMPTY') }}
                    </td>
                  </tr>
                  <tr v-for="row in section.rows" :key="row.id">
                    <td class="qa-td">{{ row.label }}</td>
                    <td class="qa-td qa-th--center">{{ row.sample_size }}</td>
                    <td class="qa-td qa-th--center">{{ row.confirmed }}</td>
                    <td class="qa-td qa-th--center">{{ row.overturned }}</td>
                    <td class="qa-td qa-th--center">{{ row.excused }}</td>
                    <td class="qa-td qa-th--center">
                      {{ pct(row.rate) }}
                      <span v-if="ciLabel(row)" class="qa-subtle">
                        ({{ ciLabel(row) }})</span
                      >
                    </td>
                    <td class="qa-td qa-th--center">
                      <span
                        class="qa-badge"
                        :class="
                          row.sufficient_sample
                            ? 'qa-badge--good'
                            : 'qa-badge--warn'
                        "
                      >
                        {{
                          row.sufficient_sample
                            ? t('QA_DASHBOARD.CALIBRATION.BADGE_SUFFICIENT')
                            : t('QA_DASHBOARD.CALIBRATION.BADGE_INSUFFICIENT')
                        }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<style scoped>
/* .qa-header and .qa-toolbar are full-bleed bands carrying their own background
   and 24px of internal padding. Everything else on this page — the two notes,
   the KPI tiles, the footnotes and the section tables — hangs straight off
   .qa-shell with no inset of its own, so it started at the card border while
   the bands above it did not.

   Applied to the children directly rather than through a .qa-content wrapper:
   that wrapper is a flex column with a 20px gap, which would also pull the
   three footnotes apart and split the section cards that currently stack. The
   report was about the edge, so only the edge changes. Vue renders no element
   for <template v-if>, so these are genuinely direct children in the DOM. */
.qa-shell > .qa-pill,
.qa-shell > .qa-error,
.qa-shell > .qa-kpis,
.qa-shell > .qa-hint,
.qa-shell > .qa-card {
  margin-left: 32px;
  margin-right: 32px;
}

/* The notes had no top margin and the first of them sat directly on the rule
   that closes .qa-header. 16px is what .qa-content puts between that rule and
   a page's first block everywhere else. Below them the toolbar contributes its
   own 12px of padding, so 4px here brings that side to a matching 16px; the
   12px they carried inline made it 24px. */
.qa-shell > .qa-pill {
  margin-top: 16px;
  margin-bottom: 4px;
}

/* Same breakpoint qaShell.css uses to pull .qa-content and the bands' own
   padding in to 16px, so the two stay aligned on narrow screens. */
@media (max-width: 1100px) {
  .qa-shell > .qa-pill,
  .qa-shell > .qa-error,
  .qa-shell > .qa-kpis,
  .qa-shell > .qa-hint,
  .qa-shell > .qa-card {
    margin-left: 16px;
    margin-right: 16px;
  }
}
</style>
